import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaGuitar, 
  FaTools, 
  FaCrown, 
  FaMusic, 
  FaHistory, 
  FaCertificate, 
  FaStore, 
  FaHandsHelping,
  FaMapMarkerAlt,
  FaClock,
  FaPhone,
  FaEnvelope,
  FaDirections
} from 'react-icons/fa';

const Hakkimizda = () => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    window.scrollTo(0, 0);
    
    return () => {
      setMounted(false);
    };
  }, []);

  // Sayfa yüklenene kadar basit bir loading
  if (!mounted) {
    return (
      <div className="min-h-screen bg-gray-900" />
    );
  }

  const features = [
    {
      icon: FaGuitar,
      title: 'El İşçiliği',
      description: 'Her gitar, ustalarımızın elinden çıkan özenli işçilikle üretilir.'
    },
    {
      icon: FaCertificate,
      title: 'Premium Kalite',
      description: 'En kaliteli ağaçlar ve elektronik bileşenler kullanıyoruz.'
    },
    {
      icon: FaHistory,
      title: 'Vintage Ruhu',
      description: 'Relic gitarlarla tarihi karakteri modern enstrümanlara taşıyoruz.'
    },
    {
      icon: FaTools,
      title: 'Teknik Destek',
      description: 'Satış sonrası bakım ve setup hizmetleri sunuyoruz.'
    }
  ];

  const brands = ['Fender', 'Gibson', 'PRS', 'Godin'];
  
  const contactInfo = [
    {
      icon: FaMapMarkerAlt,
      title: 'Adres',
      content: 'İstanbul, Üsküdar',
      link: 'https://maps.app.goo.gl/KG6z2CFfaoz7scSZ9?g_st=aw',
      isLink: true
    },
    {
      icon: FaClock,
      title: 'Çalışma Saatleri',
      content: 'Pazartesi - Cumartesi: 10:00 - 19:00\nPazar: Kapalı'
    },
    {
      icon: FaPhone,
      title: 'Telefon',
      content: '+90 530 864 8486',
      link: 'tel:+905308648486',
      isLink: true
    },
    {
      icon: FaEnvelope,
      title: 'E-posta',
      content: 'info@neoguitarstore.com',
      link: 'mailto:info@neoguitarstore.com',
      isLink: true
    }
  ];

  const openGoogleMaps = () => {
    window.open('https://maps.app.goo.gl/KG6z2CFfaoz7scSZ9?g_st=aw', '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-3 px-6 py-3 bg-gray-800 rounded-full mb-6 border border-gray-700">
                <FaCrown className="text-white" />
                <span className="text-gray-300 font-semibold">ÜSKÜDAR, İSTANBUL</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                <span className="block">Müziğin Kalbinde,</span>
                <span className="block text-white">
                  Sanatın İzinde
                </span>
              </h1>
              
              <p className="text-gray-400 text-lg md:text-xl max-w-4xl mx-auto mb-10 leading-relaxed">
                Neo Guitar, elektro gitar tutkusunu ustalıkla birleştiren, İstanbul Üsküdar'da faaliyet gösteren özel bir gitar mağazasıdır.
              </p>
            </motion.div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="relative h-full p-8 rounded-2xl border border-gray-700 bg-gray-800">
                <h2 className="text-3xl font-bold mb-6">
                  Biz Kimiz?
                </h2>
                <div className="space-y-6 text-gray-300 leading-relaxed">
                  <p>
                    Neo Guitar olarak; el işçiliği, yüksek kaliteli malzemeler ve dünya standartlarında üretim anlayışıyla gitaristlere benzersiz enstrümanlar sunmayı hedefliyoruz.
                  </p>
                  <p>
                    Markamız bünyesinde üretilen elektronik bileşenleri tercih ediyor, her gitarı; ton, sustain, ergonomi ve estetik açıdan en üst performansı verecek şekilde titizlikle hazırlıyoruz.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="relative h-full p-8 rounded-2xl border border-gray-700 bg-gray-800">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 rounded-xl bg-gray-700">
                    <FaHistory className="text-2xl" />
                  </div>
                  <h2 className="text-3xl font-bold">Relic Uzmanlığı</h2>
                </div>
                <div className="space-y-4 text-gray-300 leading-relaxed">
                  <p>
                    Özellikle relic gitar üretimi, Neo Guitar'ın uzmanlık alanlarından biridir. Vintage ruhu modern çalım konforu ile birleştiren relic gitarlarımız; doğal, dengeli ve dünya kalitesinde eskitme teknikleriyle üretilir.
                  </p>
                  <p>
                    Bu sayede her gitar, karakteri olan özgün bir enstrümana dönüşür.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Features Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-20"
          >
            <h2 className="text-3xl font-bold text-center mb-12">
              Neden Neo Guitar?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.5 + (index * 0.1) }}
                  className="p-6 rounded-2xl border border-gray-700 bg-gray-800 hover:border-gray-600 transition-all duration-300"
                >
                  <div className="p-3 rounded-xl bg-gray-700 inline-flex mb-4">
                    <feature.icon className="text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Contact & Map Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mb-20"
          >
            <h2 className="text-3xl font-bold text-center mb-12">
              Bize Ulaşın
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Contact Info */}
              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 + (index * 0.1) }}
                    className={`p-6 rounded-2xl border border-gray-700 bg-gray-800 ${
                      item.isLink ? 'cursor-pointer hover:border-gray-600 transition-all duration-300' : ''
                    }`}
                    onClick={item.isLink ? () => window.open(item.link, '_blank') : undefined}
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-gray-700">
                        <item.icon className="text-xl" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                        <p className="text-gray-300 whitespace-pre-line">{item.content}</p>
                        {item.isLink && (
                          <span className="text-white text-sm mt-2 inline-block hover:text-gray-300 transition-colors">
                            Tıklayın →
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Google Maps */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="relative rounded-2xl overflow-hidden border border-gray-700"
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3010.2795658571916!2d29.0181248!3d41.025506!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cac9e16b8a1c9f%3A0x1a8fc5a9f4e8d7b8!2sNeo%20Guitar%20Store!5e0!3m2!1str!2str!4v1698765432100!5m2!1str!2str"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="relative z-0"
                  title="Neo Guitar Store Konum"
                />
                
                <div className="absolute bottom-0 left-0 right-0 bg-gray-900/90 p-6 z-20">
                  <button
                    onClick={openGoogleMaps}
                    className="w-full py-3 px-6 bg-gray-800 hover:bg-gray-700 rounded-lg font-bold flex items-center justify-center gap-3 transition-all duration-300"
                  >
                    <FaDirections className="text-xl" />
                    Haritada Göster
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Brands & Services */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.9 }}
            >
              <div className="p-8 rounded-2xl border border-gray-700 bg-gray-800">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 rounded-xl bg-gray-700">
                    <FaStore className="text-2xl" />
                  </div>
                  <h2 className="text-3xl font-bold">Mağazamızda</h2>
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Fender, Gibson, PRS, Godin gibi dünya çapında tanınmış markaların üst segment elektro gitar modelleri yer almaktadır. Hem koleksiyonluk hem de profesyonel sahne ve stüdyo kullanımı için seçilmiş modelleri gitaristlerle buluşturuyoruz.
                </p>
                
                <div className="flex flex-wrap gap-4 mt-6">
                  {brands.map((brand, index) => (
                    <span 
                      key={index}
                      className="px-4 py-2 bg-gray-900 rounded-lg border border-gray-700 font-semibold text-white"
                    >
                      {brand}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.0 }}
            >
              <div className="p-8 rounded-2xl border border-gray-700 bg-gray-800">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 rounded-xl bg-gray-700">
                    <FaHandsHelping className="text-2xl" />
                  </div>
                  <h2 className="text-3xl font-bold">Hizmetlerimiz</h2>
                </div>
                <ul className="space-y-4 text-gray-300">
                  <li className="flex items-start gap-3">
                    <div className="p-1 rounded bg-gray-700 mt-1">
                      <div className="w-2 h-2 rounded-full bg-white" />
                    </div>
                    <span>Gitar bakım ve ayar işlemleri</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="p-1 rounded bg-gray-700 mt-1">
                      <div className="w-2 h-2 rounded-full bg-white" />
                    </div>
                    <span>Tel değişimi ve setup</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="p-1 rounded bg-gray-700 mt-1">
                      <div className="w-2 h-2 rounded-full bg-white" />
                    </div>
                    <span>Gitar yedek parçaları ve donanım satışları</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="p-1 rounded bg-gray-700 mt-1">
                      <div className="w-2 h-2 rounded-full bg-white" />
                    </div>
                    <span>Custom gitar tasarımı ve üretimi</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="p-1 rounded bg-gray-700 mt-1">
                      <div className="w-2 h-2 rounded-full bg-white" />
                    </div>
                    <span>Elektro gitar takas hizmeti</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>

          {/* Philosophy Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 1.1 }}
            className="text-center p-8 rounded-2xl bg-gray-800 border border-gray-700"
          >
            <div className="max-w-3xl mx-auto">
              <div className="p-3 rounded-xl bg-gray-700 inline-flex mb-6">
                <FaMusic className="text-3xl" />
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold mb-6">
                Bizim İçin Gitar Nedir?
              </h2>
              
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                "Neo Guitar'da her gitar bir üründen fazlasıdır. Bizim için gitar; kişisel bir ifade, bir karakter ve bir yolculuktur. Her enstrüman, sahibinin müzik yolculuğunda bir arkadaş, bir hikaye anlatıcısı ve bir sanat eseridir."
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 rounded-full border border-gray-700">
                  <span className="text-white font-semibold">Neo Guitar</span>
                  <span className="text-gray-400">- İstanbul Üsküdar</span>
                </div>
                
                <button
                  onClick={openGoogleMaps}
                  className="inline-flex items-center gap-2 px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-full border border-gray-600 text-white transition-all duration-300"
                >
                  <FaMapMarkerAlt />
                  Konuma Git
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Hakkimizda;